import { useState } from 'react';
import { InputAdornment, Theme, makeStyles } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import { BaseInput } from '../form/BaseInput';
import { SecondaryIconButton } from '../Buttons';

import { borderColor } from 'src/data/colors';
import { PointsProps } from 'src/components/modules/points/types/types';

interface IntegerInputProps {
  value: number;
  keyData: string;
  onKeyChange: (key: string, value: number) => void;
  point: PointsProps;
  range?: {
    min: number;
    max: number;
  };
  readOnly?: boolean;
  showButtons?: boolean;
  width?: string;
  label?: string;
  placeholder?: string;
  unit?: string;
}

type StyleProps = {
  showButtons: boolean;
};

const useStyles = makeStyles<Theme, StyleProps>(() => ({
  input: ({ showButtons }) => ({
    width: '100%',
    marginRight: 4,
    '& input[type=number]': {
      '-moz-appearance': 'textfield',
    },
    '& input[type=number]::-webkit-outer-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    '& input[type=number]::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
  }),
  inputButton: {
    padding: 7,
    borderRadius: 4,
    border: `1px solid ${borderColor}`,
    color: 'rgba(0, 0, 0, 0.46)',
    marginLeft: 4,
  },
}));

const IntegerInput = ({
  value,
  keyData,
  onKeyChange,
  point,
  range,
  readOnly,
  showButtons = false,
  width,
  label,
  placeholder,
  unit,
}: IntegerInputProps) => {
  const [visibleValue, setVisibleValue] = useState<string>(value.toString());
  const classes = useStyles({ showButtons });

  const adornment = unit
    ? {
        endAdornment: <InputAdornment position="end">{unit}</InputAdornment>,
      }
    : {};

  const onChange = (newValue: string) => {
    setVisibleValue(newValue);

    if (newValue === '') {
      onKeyChange(keyData, 0);
    } else {
      onKeyChange(keyData, parseInt(newValue));
    }
  };

  const handleIncrement = () => {
    setVisibleValue((Number(visibleValue) + 1).toString());
    onKeyChange('', Number(value) + 1);
  };

  const handleDecrement = () => {
    setVisibleValue((Number(visibleValue) - 1).toString());
    onKeyChange('', Number(value) - 1);
  };

  return (
    <>
      <BaseInput
        className={classes.input}
        value={visibleValue}
        onChange={(e) => onChange(e.target.value)}
        type="number"
        variant="outlined"
        disabled={point.readOnly}
        style={{ width }}
        label={label}
        placeholder={placeholder}
        fullWidth
        size="small"
        InputProps={{
          ...adornment,
        }}
      />
      {showButtons && (
        <>
          <SecondaryIconButton
            onClick={handleDecrement}
            className={classes.inputButton}
            disabled={value === Number(range?.min) || readOnly}
            size="small"
          >
            <RemoveIcon />
          </SecondaryIconButton>
          <SecondaryIconButton
            onClick={handleIncrement}
            className={classes.inputButton}
            disabled={value === Number(range?.max) || readOnly}
            size="small"
          >
            <AddIcon />
          </SecondaryIconButton>
        </>
      )}
    </>
  );
};

export default IntegerInput;
